import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useRazorpay, { RazorpayOptions } from 'react-razorpay';
import { toast } from 'react-toastify';
import format from 'string-template';

import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';
import DigitalBalanceModal from './DigitalBalanceModal';
import OtpPopup from 'shared/common/otp-modal/otpModal';
import Loader from 'shared/common/loader';
import useCountdown from 'shared/hooks/useCountDown';

import BuyCoinsBarsService from '../../services/buy-coins-bars.service';
import BuySellService from 'services/buy-sell.service';

import { usePhysicalDeliveryStore } from 'store/DeliveryStore';
import { dashboardStore } from 'store/DashboardStore';
import { userStore } from 'store/UserStore';
import '../../styles/Step3.css';

import {
  ACK_FAIL,
  ACK_SUCCESS,
  OTP_SENT_TO_REG_MOBILE,
  OTP_VERIFY_FAILURE,
  OTP_VERIFY_SUCCESS,
} from 'constants/messages.constant';
import { SUCCESS } from '../../../../constants/endpoints.constant';
import buyCoinsBarsService from '../../services/buy-coins-bars.service';

const metalsWithCode = [
  { code: 'Au', name: 'Gold' },
  { code: 'Ag', name: 'Silver' },
  { code: 'Pt', name: 'Platinum' },
  { code: 'Pd', name: 'Palladium' },
];

interface ExtendedRazorpayOptions extends RazorpayOptions {
  config?: {
    display: RazorpayOptions['config']['display'] & {
      blocks: Record<string, any>;
      sequence: string[];
      preferences: Record<string, any>;
    };
  };
}

const Step3 = ({ onPaymentComplete, prevStep }) => {
  const abortControllerRef = useRef<AbortController>(new AbortController());
  const Razorpay = useRazorpay();
  const history = useHistory();

  /** STATE */
  const [loading, setLoading] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const [showDigitalBalanceModal, setShowDigitalBalanceModal] = useState(false);
  const [isMetalBalanceChecked, setIsMetalBalanceChecked] = useState(false);
  const [usedBalanceStr, setUsedBalanceStr] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [useWallet, setUseWallet] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentProvider, setPaymentProvider] = useState('RPY');
  const [bankList, setBankList] = useState<any[]>([]);
  const [selectedBank, setSelectedBank] = useState('');
  const [isBankListLoading, setIsBankListLoading] = useState(false);
  const [isPayNowDisabled, setIsPayNowDisabled] = useState(true);
  const [isContinueDisabled, setIsContinueDisabled] = useState(true);

  const [isOTPModalShow, setIsOTPModalShow] = useState(false);
  const [otpMeta, setOtpMeta] = useState<any>({
    otpId: '',
    otp: '',
    refType: 'B2B-BUYBACK',
    expiredAt: '',
  });
  const [otpData, setOtpData] = useState<any>({
    value: '',
    ackMessage: '',
    ackMessageType: 'success',
  });

  const [razorpay, setRazorpay] = useState<any>({
    customerId: '',
    amount: null,
    paymentMethod: 'NEBK',
  });

  const { timeLeft, restart } = useCountdown(300);

  const setSummary = usePhysicalDeliveryStore(
    (state) => state.setQuotationSummary
  );
  const { selectedProduct, selectedAddress, quotationSummary } =
    usePhysicalDeliveryStore((state) => ({
      selectedProduct: state.selectedProduct,
      selectedAddress: state.selectedAddress,
      quotationSummary: state.quotationSummary,
    }));

  const [customerHolding, summary] = dashboardStore((state: any) => [
    state.customerHolding,
    state.summary,
  ]);

  const [customerId, user, customer, selectedAccount] = userStore(
    (state: any) => [
      state.customerId,
      state.user,
      state.customer,
      state.selectedAccount,
    ]
  );
  razorpay.customerId = customerId;

  const matchedMetal = useMemo(
    () =>
      customerHolding.find(
        (item) => item?.metal === selectedProduct?.metalCode
      ),
    [customerHolding, selectedProduct]
  );

  // const availableBalance: number = matchedMetal
  //   ? parseFloat(
  //     (matchedMetal?.quantity - matchedMetal?.lockQuantity).toFixed(4)
  //   )
  //   : 0;

  const availableBalance = matchedMetal
    ? matchedMetal.lockQuantity > 0
      ? matchedMetal.quantity // lock qty > 0 → show metal balance
      : matchedMetal.quantity - Math.abs(matchedMetal.lockQuantity) // lock qty < 0 → subtract lock qty
    : 0;

  const metalName =
    metalsWithCode.find((item) => item?.code === selectedProduct?.metalCode)
      ?.name || '';

  /** ----------------- HANDLERS ----------------- */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '' || /^\d*\.?\d*$/.test(val)) setUsedBalanceStr(val);
  };

  const addToCartWithDigitalQty = async (digitalUsedQtyValue: number) => {
    if (
      !selectedProduct?.itemCode ||
      !customer?.accounts[0]?.accountId ||
      !selectedAddress?.pinCode ||
      !user?.userId
    ) {
      toast.error('Missing required information to proceed.');
      return;
    }
    const payload = {
      productId: selectedProduct?.itemCode,
      qty: '1',
      ebActRef: customer?.accounts[0]?.accountId,
      pincode: selectedAddress?.pinCode,
      userId: user?.userId,
      digitalUsedQty: digitalUsedQtyValue,
    };

    setLoading(true);
    try {
      const response = await BuyCoinsBarsService.addProductToCart(payload);
      if (response.status === 200) {
        const summaryResponse = await BuyCoinsBarsService.getProductCartSummery(
          user.userId
        );
        if (summaryResponse.status === 202)
          setSummary(summaryResponse.data.data[0]);
        else toast.error('Failed to fetch cart summary.');
      } else toast.error('Failed to add product to cart.');
    } catch (error) {
      setIsMetalBalanceChecked(false);
      toast.error('Something went wrong!');
    } finally {
      setLoading(false);
    }
  };



  const handleUseMetalBalance = async () => {
    setShowDigitalBalanceModal(false);
    setIsMetalBalanceChecked(true);

    const redeemedQty = parseFloat(usedBalanceStr) || 0;
    await addToCartWithDigitalQty(redeemedQty);
  };

  const handleCancelClick = async () => {
    setShowDigitalBalanceModal(false);
    setIsMetalBalanceChecked(false);
    setUsedBalanceStr('0');
    await addToCartWithDigitalQty(0);
  };



  const handleWalletChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setUseWallet(checked);

    if (checked) {
      // If wallet selected, reset any chosen payment method
      setPaymentMethod('');
    } else {
      // If wallet unselected, allow user to reselect radios
      setPaymentMethod('');
    }
  };

  const handlePaymentMethodChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    // If wallet is ON but insufficient balance, allow mixed payment
    const payable = quotationSummary?.deliveryQuotationTotal || 0;
    const walletBal = summary?.walletBalance || 0;

    if (useWallet && walletBal >= payable) {
      // Wallet already covers full amount — no need for UPI/NEBK
      setPaymentMethod('');
      return;
    }

    // Otherwise, allow selecting payment method
    setPaymentMethod(value);
  };

  const fetchBankList = async () => {
    setIsBankListLoading(true);
    try {
      const res: any = await BuySellService.getNetBankingBankList();
      if (res.status === 200 || res.status === 202) {
        setBankList(res.data?.data || []);
      } else {
        toast.error('Failed to fetch bank list.');
      }
    } catch (err) {
      toast.error('Something went wrong while fetching bank list.');
    } finally {
      setIsBankListLoading(false);
    }
  };

  const generateOtp = async (ruleType = 'SELL', otpId = null) => {
    if (!user?.mobile?.value) return;
    try {
      setLoading(true);
      const res: any = await BuySellService.generateOtp(
        user.mobile.value,
        ruleType,
        otpId,
        user?.userId
      );
      setLoading(false);
      if (res.status === 200 && res.data.data) {
        setIsOTPModalShow(true);
        setOtpMeta({
          ...otpMeta,
          otpId: res.data.data.otpId,
          expiredAt: res.data.data.expiredAt,
        });
        return toast.success('OTP sent to your registered mobile number.');
      }
    } catch (e: any) {
      setLoading(false);
      toast.error(e.data.errors[0].title);
    }
  };


  const onSubmitOTP = async () => {
    try {
      setLoading(true);
      const verifyPayload = {
        otpId: otpMeta?.otpId,
        otp: parseInt(otpData?.value),
      };
      const res: any = await BuySellService.verifyOTP(
        verifyPayload,
        user?.userId
      );

      if (res.status === 200 && res.data.data) {
        setOtpData({
          value: '',
          ackMessage: OTP_VERIFY_SUCCESS,
          ackMessageType: ACK_SUCCESS,
        });
        setIsOTPModalShow(false);

        const basePayload = {
          userId: user?.userId,
          cartId: quotationSummary?.cartId,
          addressId: selectedAddress?.addressId,
        };

        // ✅ Wallet flow
        if (getPaymentMethod() === 'CUSTOMER_WALLET') {
          await placeOrder({
            ...basePayload,
            otp: parseInt(otpData?.value),
            otpId: otpMeta?.otpId,
            paymentMethod: 'CUSTOMER_WALLET',
          });
        }
        //  Digital Metal balance flow
        else if (isMetalBalanceChecked) {
          // Decide if remaining payment is via Razorpay
          const payMethod = getPaymentMethod(); // NEBK / UPI
          await placeOrder({
            ...basePayload,
            paymentMethod: payMethod,
            ...(payMethod.includes('NEBK') && { paymentProvider }),
          });
        }
      } else throw new Error('OTP verification failed');
    } catch (e: any) {
      setOtpData({
        value: '',
        ackMessage: OTP_VERIFY_FAILURE,
        ackMessageType: ACK_FAIL,
      });
      toast.error(e?.data?.errors?.[0]?.title || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const resetOtpData = () => {
    setOtpData({
      value: '',
      ackMessage: '',
      ackMessageType: 'success',
    });
  };

  const resendOTP = async () => {
    generateOtp('SELL', otpMeta.otpId);
  };

  const getPaymentMethod = (): string => {
    if (useWallet && paymentMethod === 'UPI') return 'WALLET_UPI';
    if (useWallet && paymentMethod === 'NEBK') return 'WALLET_NEBK';
    if (!useWallet && paymentMethod === 'UPI') return 'UPI';
    if (!useWallet && paymentMethod === 'NEBK') return 'NEBK';
    if (useWallet && !paymentMethod) return 'CUSTOMER_WALLET';
    return '';
  };

  const isPaymentValid = (): boolean => {
    const payable = quotationSummary.deliveryQuotationTotal;
    const walletBal = summary?.walletBalance;
    if (walletBal === 0 && !paymentMethod) return false;
    if (useWallet && walletBal >= payable) return true;
    if (useWallet && walletBal < payable && paymentMethod) return true;
    if (!useWallet && paymentMethod) return true;
    return false;
  };

  const handlePayNowClick = async () => {
    const payMethod = getPaymentMethod();

    if (!payMethod) {
      return toast.error('Invalid payment method combination.');
    }

    const payableAmount = quotationSummary?.deliveryQuotationTotal || 0;
    const walletBalance = summary?.walletBalance || 0;

    const res = await buyCoinsBarsService.checkPaymentLimit();

    if (res.status !== 200 || !res.data?.data?.payLimits) {
      return toast.error('Unable to fetch payment limits. Please try again.');
    }

    const { payLimits, currentUpiProvider } = res.data.data;

    //  Find limit for selected payment method
    let limitRule = null;

    if (payMethod === 'UPI') {
      // pick the provider from API response using currentUpiProvider
      limitRule = payLimits.find(
        (item) => item.method === 'UPI' && item.provider === currentUpiProvider
      );
    } else if (payMethod === 'WALLET_UPI') {
      // WALLET_UPI = WALLET + UPI → validate both separately
      const walletRule = payLimits.find((item) => item.method === 'WALLET');
      const upiRule = payLimits.find(
        (item) => item.method === 'UPI' && item.provider === currentUpiProvider
      );

      // Wallet check
      if (walletRule && walletBalance < walletRule.minLimit) {
        return toast.error(
          `Wallet requires at least ₹${walletRule.minLimit} balance.`
        );
      }

      // UPI check (only for amount deducted via UPI)
      const upiAmount = payableAmount - walletBalance;
      if (
        upiRule &&
        (upiAmount < upiRule.minLimit || upiAmount > upiRule.maxLimit)
      ) {
        return toast.error(
          `UPI transaction must be between ₹${upiRule.minLimit} and ₹${upiRule.maxLimit}.`
        );
      }
    } else {
      // Other payment methods
      limitRule = payLimits.find((item) => item.method === payMethod);
    }

    //  Validate normal methods
    if (limitRule) {
      if (payableAmount < limitRule.minLimit) {
        return toast.error(
          `Minimum amount for ${payMethod} is ₹${limitRule.minLimit}.`
        );
      }
      if (limitRule.maxLimit !== -1 && payableAmount > limitRule.maxLimit) {
        return toast.error(
          `Maximum amount for ${payMethod} is ₹${limitRule.maxLimit}.`
        );
      }
      if (!limitRule.canProceed) {
        return toast.error(
          `Payment via ${payMethod} is temporarily unavailable.`
        );
      }
    }

    // Validate wallet / payment method combination
    if (!isPaymentValid()) {
      return toast.error(
        'Invalid payment method or insufficient wallet balance.'
      );
    }

    const payload = {
      userId: user?.userId,
      cartId: quotationSummary?.cartId,
      paymentMethod: payMethod,
      addressId: selectedAddress?.addressId,
      ...(payMethod.includes('NEBK') && { paymentProvider }),
    };

    // Handle Wallet OTP flow
    if (payMethod === 'CUSTOMER_WALLET' || isMetalBalanceChecked) {
      await generateOtp('SELL');
      setIsOTPModalShow(true);
      return;
    }

    // Place order via API or Razorpay
    await placeOrder(payload);
  };

  const placeOrder = async (payload: any) => {
    try {
      setLoading(true);
      const response: any = await BuyCoinsBarsService.deliveryOrder(payload);

      if (response.status === 200 || response.status === 202) {
        //  Handle CUSTOMER_WALLET flow directly
        if (payload.paymentMethod === 'CUSTOMER_WALLET') {
          const orderStatus = response?.data?.data?.status;
          if (orderStatus === 'COMPLETED') {
            toast.success('Order placed successfully via Wallet!');
            onPaymentComplete?.(true);
          } else {
            toast.error('Wallet order failed. Please try again.');
            onPaymentComplete?.(false);
          }
          return; // stop here, no Razorpay flow
        }

        //  For UPI / NEBK continue existing Razorpay handling
        setPaymentGatewayConfiguration(response.data.data);
        setIsOTPModalShow(false);
      } else {
        toast.error('Failed to place order');
        onPaymentComplete?.(false);
      }
    } catch (err) {
      toast.error('Something went wrong while placing the order.');
      onPaymentComplete?.(false);
    } finally {
      setLoading(false);
    }
  };

  const openEasebuzzCheckout = (accessKey: string, orderRef: string) => {
    if (!accessKey || !orderRef) {
      setLoading(false);
      toast.error('Unable to start payment. Please try again.');
      return;
    }
    const env = process.env.REACT_APP_EASEBUZZ_ENV === 'prod' ? 'prod' : 'test';
    const baseUrl =
      env === 'prod'
        ? 'https://pay.easebuzz.in/pay/'
        : 'https://testpay.easebuzz.in/pay/';
    const checkoutUrl = `${baseUrl}${accessKey}`;

    sessionStorage.setItem('easebuzzOrderRef', orderRef);
    sessionStorage.setItem('easebuzzReturnPath', '/buy-coins-bars');
    window.location.href = checkoutUrl;
  };

  const setPaymentGatewayConfiguration = async (data) => {
    setLoading(false);
    const paymentMeta =
      data?.data?.payload?.data?.meta?.payment?.response?.data?.meta || [];
    const amount = parseFloat(
      data?.data?.payload?.data?.meta?.payment?.response?.data?.amount || '0'
    );
    const provider = paymentMeta?.find(
      (entry) => entry.key === 'PAYMENT_PROVIDER'
    )?.value;
    const orderId = paymentMeta.find(
      (entry) => entry.key === 'RPY_ORDER_ID'
    )?.value;

    if (provider === 'EBZ') {
      const accessKey = paymentMeta.find(
        (entry) => entry.key === 'EBZ_ACCESS_KEY'
      )?.value;
      return openEasebuzzCheckout(
        accessKey,
        data?.data?.payload?.data?.ebOrderRef
      );
    }

    razorpay.amount = amount;
    const options: ExtendedRazorpayOptions = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: (amount * 100).toString(),
      currency: 'INR',
      name: 'eBullion',
      description: 'Buy Metals',
      image:
        'https://ebullion-dev.s3.ap-south-1.amazonaws.com/assets/eb-logo.png',
      order_id: orderId || '',
      async handler(response: any) {
        const payload = {
          customerId: razorpay.customerId,
          razorpayOrderId: response.razorpay_order_id,
          razorpayPaymentId: response.razorpay_payment_id,
          razorpaySignature: response.razorpay_signature,
          razorpayRequestBody: { amount: razorpay.amount, currency: 'INR' },
        };
        setInitialStateRazorpay();
        await checkPaymentStatus(
          response.razorpay_payment_id,
          data?.data?.payload?.data?.ebOrderRef
        );
      },
      prefill: {
        name: user.displayName,
        email: user.email.value,
        contact: user.mobile.value,
        method: paymentMethod.includes('UPI') ? 'upi' : 'netbanking',
      },
      // modal: { ondismiss: () => window.location.reload() } as any,
      modal: {
        ondismiss: () => {
          toast.error('Payment cancelled!');
          onPaymentComplete?.(false);
        },
      } as any,
      config: {
        display: {
          language: 'en',
          blocks: {
            banks: {
              name:
                paymentMethod === 'UPI' ? 'Pay via UPI' : 'Pay via Net Banking',
              instruments: [
                {
                  method: paymentMethod.includes('UPI') ? 'upi' : 'netbanking',
                },
              ],
            },
          },
          sequence: ['block.banks'],
          preferences: { show_default_blocks: false },
        },
      },
      notes: {
        address:
          selectedAccount?.customers?.[0]?.kyc?.aadhaar?.data?.address
            ?.address1 || '',
      },
      theme: {
        hide_topbar: false,
        color: '#141034',
        backdrop_color: 'rgba(0, 0, 0, 0.90)',
      },
      timeout: 180,
    };

    if (
      (paymentMethod.includes('UPI') && provider === 'RPY') ||
      paymentMethod.includes('NEBK')
    ) {
      const razorPay = new Razorpay(options);
      return razorPay.open();
    }
  };

  const setInitialStateRazorpay = () =>
    setRazorpay({
      customerId,
      amount: null,
      transactionTypeId: 'eb-o-00333',
      paymentMethod: 'NEBK',
    });

  const checkPaymentStatus = async (paymentId, orderId) => {
    try {
      setLoading(true);
      const res = await BuySellService.checkPaymentStatus(
        { paymentId, orderId },
        abortControllerRef.current.signal
      );

      if (res?.status === 200 && res.data?.data?.paymentStatus === SUCCESS) {
       toast.success('Order Placed Successfully!');
        onPaymentComplete?.(true);
      } else {
        toast.error('Payment failed or cancelled');
        onPaymentComplete?.(false);
      }
    } catch (err) {
      toast.error('Something went wrong while verifying payment.');
      onPaymentComplete?.(false);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const payable = quotationSummary?.deliveryQuotationTotal || 0;
    const walletBal = summary?.walletBalance || 0;

    // CASE 1: Wallet only
    if (useWallet && walletBal >= payable) {
      setIsPayNowDisabled(false);
      return;
    }

    // CASE 2: Wallet + another method
    if (useWallet && walletBal < payable && paymentMethod) {
      setIsPayNowDisabled(false);
      return;
    }

    // CASE 3: Non-wallet methods
    if (!useWallet && paymentMethod) {
      setIsPayNowDisabled(false);
      return;
    }

    //  CASE 4: No valid payment method chosen
    setIsPayNowDisabled(true);
  }, [
    useWallet,
    paymentMethod,
    summary?.walletBalance,
    quotationSummary?.deliveryQuotationTotal,
  ]);

  useEffect(() => {
    if (timeLeft === 0 && user?.userId) {
      (async () => {
        try {
          const summaryResponse =
            await BuyCoinsBarsService.getProductCartSummery(user.userId);
          if (summaryResponse.status === 202) {
            setSummary(summaryResponse.data.data[0]);

            // Reapply redeemed balance if user had checked it
            if (isMetalBalanceChecked && usedBalanceStr) {
              await addToCartWithDigitalQty(parseFloat(usedBalanceStr) || 0);
            }
          }
          restart();
        } catch (err) {
          console.error(err);
        }
      })();
    }
  }, [timeLeft]);


  useEffect(() => {
    const num = parseFloat(usedBalanceStr);
    const productWeight = parseFloat(selectedProduct.weight);
    const balance = parseFloat(availableBalance);

    if (
      !usedBalanceStr ||
      isNaN(num) ||
      num <= 0 ||
      num > productWeight ||
      parseFloat(num.toFixed(4)) > parseFloat(balance.toFixed(4))
    ) {
      setErrorMessage(
        num <= 0
          ? 'Please enter a value greater than 0'
          : num > productWeight
            ? 'Quantity should be less than redemption quantity'
            : parseFloat(num.toFixed(4)) > parseFloat(balance.toFixed(4))
              ? `Insufficient balance. Max: ${balance.toFixed(4)} g`
              : ''
      );
      setIsContinueDisabled(true);
      return;
    }

    setErrorMessage('');
    setIsContinueDisabled(false);
  }, [usedBalanceStr, selectedProduct.weight, availableBalance]);


  useEffect(() => {
    if (otpData.value?.length === 6) onSubmitOTP();
  }, [otpData.value]);

  useEffect(() => {
    if (paymentMethod === 'NEBK' && bankList.length === 0) {
      fetchBankList();
    }
    if (paymentMethod !== 'NEBK') {
      setSelectedBank('');
    }
  }, [paymentMethod]);

  return (
    <div className='step3-container'>
      {loading && <Loader />}

      <LeftPanel
        selectedAddress={selectedAddress}
        wallet={summary?.walletBalance}
        useWallet={useWallet}
        isWalletSufficient={
          summary?.walletBalance >= quotationSummary?.deliveryQuotationTotal
        }
        paymentMethod={paymentMethod}
        paymentProvider={paymentProvider}
        setPaymentProvider={setPaymentProvider}
        bankList={bankList}
        selectedBank={selectedBank}
        setSelectedBank={setSelectedBank}
        isBankListLoading={isBankListLoading}
        handleWalletChange={handleWalletChange}
        handlePaymentMethodChange={handlePaymentMethodChange}
        isAccordionOpen={isAccordionOpen}
        setIsAccordionOpen={setIsAccordionOpen}
        selectedProduct={selectedProduct}
      />

      <RightPanel
        metalName={metalName}
        timer={timeLeft}
        summary={quotationSummary}
        customerHolding={customerHolding}
        selectedProduct={selectedProduct}
        addToCartWithDigitalQty={addToCartWithDigitalQty}
        setShowDigitalBalanceModal={setShowDigitalBalanceModal}
        isMetalBalanceChecked={isMetalBalanceChecked}
        setIsMetalBalanceChecked={setIsMetalBalanceChecked}
        isPayNowDisabled={isPayNowDisabled}
        handlePayNowClick={handlePayNowClick}
      />

      <div className='step-navigation'>
        <button className='previous-button' onClick={prevStep}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='14'
            height='14'
            fill='currentColor'
            viewBox='0 0 16 16'
            className='previous-icon'
          >
            <path
              fillRule='evenodd'
              d='M11 2a.5.5 0 0 1 0 .707L6.207 7.5H14.5a.5.5 0 0 1 0 1H6.207l4.793 4.793a.5.5 0 0 1-.707.707l-5.5-5.5a.5.5 0 0 1 0-.707l5.5-5.5A.5.5 0 0 1 11 2z'
            />
          </svg>
          Previous
        </button>
      </div>

      {showDigitalBalanceModal && (
        <DigitalBalanceModal
          metalName={metalName}
          availableBalance={availableBalance}
          usedBalance={parseFloat(usedBalanceStr) || 0}
          usedBalanceStr={usedBalanceStr}
          errorMessage={errorMessage}
          handleInputChange={handleInputChange}
          isContinueDisabled={isContinueDisabled}
          handleUseMetalBalance={handleUseMetalBalance}
          handleCancelClick={handleCancelClick}
          sellQuantity={parseFloat(selectedProduct?.weight) || 0}
        />
      )}

      <OtpPopup
        show={isOTPModalShow}
        isLoading={loading}
        handleClose={() => {
          resetOtpData();
          setIsOTPModalShow(false);
        }}
        modalTitle={`Transaction Confirmation`}
        modalSubmitBtnText='Verify'
        onSubmit={() => onSubmitOTP()}
        otpLabel={format(OTP_SENT_TO_REG_MOBILE, {
          suffixDigits: user?.mobile?.value?.slice(-4),
        })}
        enableResendTimer={true}
        resendHandler={() => {
          resetOtpData();
          resendOTP();
        }}
        otpChangeHandler={(value) => {
          setOtpData({
            value: value,
            ackMessage: '',
            ackMessageType: 'success',
          });
        }}
        resendButtonText='Resend OTP'
        ackMessage={otpData.ackMessage}
        ackMessageType={otpData.ackMessageType}
        numInputs={6}
        otpValue={otpData.value}
        resendInterval={30}
      />
    </div>
  );
};

export default Step3;
